import { defineStore } from "pinia";
import api from "../api";
import { onServerEvent } from "../eventStream";
import type { RouteMapItem } from "../types/routes";

// Вне state стора: это учёт текущего запроса, реактивность не нужна.
let inFlight: Promise<void> | null = null;
let subscribedToUpdates = false;

export const useRoutesStore = defineStore("routes", {
  state: () => ({
    items: [] as RouteMapItem[],
    loading: false,
    loaded: false,
    // Последняя загрузка не удалась (сеть): items пуст, loaded = true - прочие
    // потребители видят пустой список; экран поиска показывает «Повторить» (retry()).
    error: false,
  }),
  actions: {
    // Единая точка входа для всех потребителей: первый вызов делает запрос,
    // остальные получают кэш или ждут тот же in-flight промис.
    async fetch() {
      if (this.loaded) return;
      if (inFlight) return inFlight;
      this.loading = true;
      inFlight = (async () => {
        try {
          const res = await api.get<RouteMapItem[]>("/routes");
          this.items = res.data || [];
          this.error = false;
        } catch {
          this.items = [];
          this.error = true;
        } finally {
          this.loading = false;
          this.loaded = true;
          inFlight = null;
        }
      })();
      this._subscribeToUpdates();
      return inFlight;
    },
    // Повторная загрузка после ошибки - со скелетонами, как первая.
    async retry() {
      if (inFlight) return inFlight;
      this.loaded = false;
      this.error = false;
      return this.fetch();
    },
    // Не трогает loading/loaded - используется для SSE-обновления, чтобы
    // изменение маршрута админом не показывало скелетоны у уже смонтированных потребителей.
    async refresh() {
      try {
        const res = await api.get<RouteMapItem[]>("/routes");
        this.items = res.data || [];
        this.error = false;
      } catch {
        // фоновое обновление - при ошибке просто оставляем текущее состояние
      }
    },
    _subscribeToUpdates() {
      if (subscribedToUpdates) return;
      subscribedToUpdates = true;
      onServerEvent("route", () => this.refresh());
    },
  },
});
